import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Close } from '@mui/icons-material';
import { Plus } from 'react-feather';
import { IconButton, Stack, Typography, useTheme } from '@mui/material';

// Components
import LineChart from 'src/components/HighChart/LineChart';

// Utilities
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { useQueryDataTurnaroundChart } from 'src/services/queries';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { ITurnaround } from 'src/utils/typings/response';
import { StyledWhiteBox } from 'src/features/PortDetail/config';
import IconFeather from 'src/components/common/Icon';
import Table from 'src/components/Table';

interface IProps {
  locode: string;
  countryVerbose: string;
}

const TurnaroundChart = ({ locode = '', countryVerbose = '' }: IProps) => {
  const theme = useTheme();
  const [showTableTurnaroundChart, setShowTurnaroundChart] = useState(false);
  const { data, isFetching } = useQueryDataTurnaroundChart({ locode });
  const dataTurnaroundChart = getArrayDefault(data?.data);

  const lineChartYData = useMemo(() => {
    return dataTurnaroundChart.map((item: ITurnaround) => [item.turnaround]);
  }, [dataTurnaroundChart]);

  const lineChartXData = useMemo(() => {
    const res = dataTurnaroundChart.map((item: ITurnaround) => {
      return dayjs(item?.date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.FULL_ONLY_DATE);
    });
    return res;
  }, [dataTurnaroundChart]);

  const configColumn: ConfigCell<ITurnaround>[] = [
    {
      id: 'date',
      disablePadding: true,
      disableSort: true,
      label: 'Date',
      cell: (row: ITurnaround) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(row?.date)}
          </Typography>
        );
      },
    },
    {
      id: 'turnaround',
      disablePadding: true,
      disableSort: true,
      label: 'Turnaround',
      cell: (row: ITurnaround) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(row?.turnaround)}
          </Typography>
        );
      },
    },
  ];

  return (
    <StyledWhiteBox display="flex" flexDirection="column" gap={2}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h5" align="left">
          Turnaround weighted by TEU in {countryVerbose}
        </Typography>
        <IconButton onClick={() => setShowTurnaroundChart(!showTableTurnaroundChart)} color="primary">
          {showTableTurnaroundChart ? <Close /> : <IconFeather icon={<Plus />} />}
        </IconButton>
      </Stack>
      {showTableTurnaroundChart ? (
        <Table
          data={dataTurnaroundChart}
          configColumn={configColumn}
          dataKey="date"
          isPagination={false}
          maxRow={10}
          loading={isFetching}
          totalRecords={getArrayDefault(dataTurnaroundChart).length}
          sxTableContainer={{
            border: `1px solid ${theme.palette.divider}`,
          }}
        />
      ) : (
        <LineChart
          loading={isFetching}
          data={lineChartYData}
          dataPropertyKeys={['y', 'custom.number_of_suppliers', 'custom.number_of_buyers']}
          tooltipFormatter={function (this) {
            return `<b>${this.x}</b><br>
          <span style="color: ${theme.palette.text.secondary}">Average time in port during portcalls (in days): <b style="color: white">${renderNumberOrDefault(this.y)}</b></span><br>`;
          }}
          xAxis={{
            allowDecimals: false,
            labels: {
              formatter: function () {
                const month = String(this.value).split('/')[1]; // Extract month from categories
                const year = String(this.value).split('/')[2]; // Extract year from categories
                return `${month}/${year}`;
              },
            },
            showLastLabel: false,
            tickInterval: 4, // Show one tick per mont
            categories: lineChartXData,
          }}
        />
      )}
    </StyledWhiteBox>
  );
};

export default TurnaroundChart;
